import { useState, useEffect, useRef, useCallback } from "react";
import api, { errMsg, fmtDate } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Paperclip, Download, Trash2, FileImage, FileText, FileSpreadsheet, Upload } from "lucide-react";

const EVIDENCE_ACCEPT = ".jpg,.jpeg,.png,.webp,.gif,.pdf,.xls,.xlsx,.csv";
const MAX_BYTES = 15 * 1024 * 1024;

function evidenceIcon(ext = "") {
  const e = (ext || "").toLowerCase();
  if (["jpg", "jpeg", "png", "webp", "gif"].includes(e)) return <FileImage size={14} className="text-primary" />;
  if (["xls", "xlsx", "csv"].includes(e)) return <FileSpreadsheet size={14} className="text-emerald-600" />;
  return <FileText size={14} className="text-muted-foreground" />;
}

function formatSize(bytes = 0) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

/**
 * Generic evidence attachments panel.
 *
 * Props:
 *  - resourceType: string used only for data-testid prefix (e.g. "cc", "vale")
 *  - basePath: API path prefix (e.g. `/cycle-counts/{id}/evidence` or `/vales/{id}/evidence`)
 *  - canWrite: whether current user can upload/delete
 *  - hideDelete: optionally hide the delete action even for canWrite users
 */
export function EvidencePanel({ resourceType, basePath, canWrite = true, hideDelete = false }) {
  const { t } = useI18n();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    if (!basePath) return;
    try {
      const { data } = await api.get(basePath);
      setItems(data);
    } catch {
      setItems([]);
    }
  }, [basePath]);

  useEffect(() => {
    load();
  }, [load]);

  const upload = async (file) => {
    if (!file) return;
    if (file.size > MAX_BYTES) {
      toast.error("Max 15 MB");
      return;
    }
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      await api.post(basePath, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("cc.evidenceUploaded"));
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const download = async (ev) => {
    try {
      const res = await api.get(`${basePath}/${ev.id}/download`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = ev.original_filename || `evidence.${ev.extension || "bin"}`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async (ev) => {
    if (!window.confirm(t("cc.evidenceConfirmDelete"))) return;
    try {
      await api.delete(`${basePath}/${ev.id}`);
      toast.success(t("cc.evidenceDeleted"));
      await load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const tid = (suffix) => `${resourceType}-evidence-${suffix}`;

  return (
    <div className="border-t pt-3 mt-1" data-testid={tid("section")}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Paperclip size={14} className="text-primary" />
          <span className="text-[11px] font-black uppercase tracking-widest">{t("cc.evidences")}</span>
          <span className="text-[10px] text-muted-foreground">({items.length})</span>
        </div>
        {canWrite && (
          <>
            <input
              ref={fileInputRef}
              type="file"
              accept={EVIDENCE_ACCEPT}
              onChange={(e) => upload(e.target.files?.[0])}
              className="hidden"
              data-testid={tid("file-input")}
            />
            <Button
              size="sm"
              variant="outline"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
              className="rounded-none text-[10px] font-bold h-7"
              data-testid={tid("upload-btn")}
            >
              <Upload size={12} className="mr-1" />
              {uploading ? "…" : t("cc.evidenceUpload")}
            </Button>
          </>
        )}
      </div>
      <p className="text-[10px] text-muted-foreground mb-2">{t("cc.evidenceHint")}</p>
      {items.length === 0 ? (
        <p className="text-[11px] text-muted-foreground italic py-2" data-testid={tid("empty")}>{t("cc.evidenceEmpty")}</p>
      ) : (
        <div className="tbl-wrap max-h-48 overflow-y-auto" data-testid={tid("list")}>
          <table className="tbl">
            <thead>
              <tr>
                <th></th>
                <th>Archivo</th>
                <th className="num">Tamaño</th>
                <th>Usuario</th>
                <th>Fecha</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items.map((ev) => (
                <tr key={ev.id} data-testid={`${resourceType}-evidence-row-${ev.id}`}>
                  <td>{evidenceIcon(ev.extension)}</td>
                  <td className="font-bold text-[11px]">{ev.original_filename}</td>
                  <td className="num text-[11px]">{formatSize(ev.size)}</td>
                  <td className="text-muted-foreground text-[10px]">{ev.uploaded_by}</td>
                  <td className="text-muted-foreground text-[10px]">{fmtDate(ev.uploaded_at)}</td>
                  <td className="flex gap-2">
                    <button
                      onClick={() => download(ev)}
                      className="text-primary hover:underline text-[10px] font-bold flex items-center gap-1"
                      data-testid={`${resourceType}-evidence-download-${ev.id}`}
                    >
                      <Download size={11} /> {t("cc.evidenceDownload")}
                    </button>
                    {canWrite && !hideDelete && (
                      <button
                        onClick={() => remove(ev)}
                        className="text-destructive hover:underline text-[10px] font-bold flex items-center gap-1"
                        data-testid={`${resourceType}-evidence-delete-${ev.id}`}
                      >
                        <Trash2 size={11} /> {t("cc.evidenceDelete")}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
